import { LocalParticipant, Participant, RemoteParticipant } from 'livekit-client';
import { action, observable, makeAutoObservable, computed } from 'mobx';

class ParticipantsStore {
    @observable public screenShareParticipants: Map<string, Participant> = new Map<string, Participant>();
    @observable public localParticipant: LocalParticipant | null = null;
    @observable public remoteParticipants: Map<string, RemoteParticipant> = new Map<string, RemoteParticipant>();

    public constructor() {
        makeAutoObservable(this);
    }

    @action
    public addScreenShareParticipant = (participant: Participant) => {
        const id = participant.identity;

        if (!this.screenShareParticipants.has(id)) {
            this.screenShareParticipants.set(id, participant);
        }
    };

    @action
    public removeScreenShareParticipant = (participant: Participant) => {
        const id = participant.identity;

        if (this.screenShareParticipants.has(id)) {
            this.screenShareParticipants.delete(id);
        }
    };

    @action
    public setLocalParticipant = (localParticipant: LocalParticipant) => {
        this.localParticipant = localParticipant;
    };

    @action
    public addRemoteParticipant = (remoteParticipant: RemoteParticipant) => {
        this.remoteParticipants.set(remoteParticipant.identity, remoteParticipant);
    };

    @action
    public removeRemoteParticipant = (remoteParticipant: RemoteParticipant) => {
        this.remoteParticipants.delete(remoteParticipant.identity);
    };

    @action
    public setRemoteParticipants = (remoteParticipants: Map<string, RemoteParticipant>) => {
        this.remoteParticipants = remoteParticipants;
    };

    @action
    public updateRemoteParticipant = (remoteParticipant: RemoteParticipant) => {
        if (this.remoteParticipants.has(remoteParticipant.identity)) {
            this.remoteParticipants.set(remoteParticipant.identity, remoteParticipant);
        }
    };

    @action
    public updateRemoteParticipants = (remoteParticipants: RemoteParticipant[]) => {
        remoteParticipants.forEach((participant) => {
            this.updateRemoteParticipant(participant);
        });
    };

    @action
    public setLocalParticipantProperty = <K extends keyof LocalParticipant>(
        property: K,
        value: LocalParticipant[K],
    ) => {
        if (this.localParticipant) {
            this.localParticipant[property] = value;
        }
    };

    @action
    public setRemoteParticipantProperty = <K extends keyof RemoteParticipant>(
        identity: string,
        property: K,
        value: RemoteParticipant[K],
    ) => {
        const participant = this.remoteParticipants.get(identity);

        if (participant) {
            participant[property] = value;
        }
    };

    @computed
    public get isScreenShareEnabled() {
        return this.screenShareParticipants.size > 0;
    }
}

export const participantsStore = new ParticipantsStore();
